import type { AreaSport } from "./graph-types";
import type { PlannerObjectives, PlannerPreferences } from "./preferences";

export type SkiDayStyle =
  | "locals-best"
  | "cruisy-classics"
  | "steep-technical"
  | "cover-ground"
  | "quiet-day"
  | "sun-and-views"
  | "easy-social"
  | "best-snow";

export type MtbDayStyle =
  | "locals-best-ride"
  | "flow-day"
  | "technical-enduro"
  | "big-descents"
  | "xc-adventure"
  | "quiet-uplift"
  | "easy-social-ride"
  | "best-dirt";

export type DayStyle = SkiDayStyle | MtbDayStyle;

export type DifficultyFitCurve = {
  byDelta: Partial<Record<"-4" | "-3" | "-2" | "-1" | "0" | "+1", number>>;
};

export type DayStyleConfig = {
  key: DayStyle;
  sport: AreaSport;
  label: string;
  icon: string;
  blurb: string;
  summaryLine: string;
  objectives: Partial<PlannerObjectives>;
  difficultyFit: DifficultyFitCurve;
  keepGreens?: boolean;
  appealWeight: number;
  noveltyBase: number;
  repeatSoftCap: number;
  queueWeight: number;
  sectorEntryBonus: number;
  stopBiasMultiplier: number;
  transferPenaltyMultiplier: number;
  surfaceBias?: { groomed?: number; ungroomed?: number };
  dropBonus?: boolean;
  prefPatch: Partial<PlannerPreferences>;
};

const NEUTRAL = {
  objectives: {},
  difficultyFit: { byDelta: {} },
  appealWeight: 1,
  noveltyBase: 0.45,
  repeatSoftCap: 0.7,
  queueWeight: 1,
  sectorEntryBonus: 0,
  stopBiasMultiplier: 1,
  transferPenaltyMultiplier: 1,
  prefPatch: {},
} satisfies Pick<
  DayStyleConfig,
  | "objectives"
  | "difficultyFit"
  | "appealWeight"
  | "noveltyBase"
  | "repeatSoftCap"
  | "queueWeight"
  | "sectorEntryBonus"
  | "stopBiasMultiplier"
  | "transferPenaltyMultiplier"
  | "prefPatch"
>;

export const W = 0.6;

function patch(value: Partial<PlannerPreferences>): Partial<PlannerPreferences> {
  return value;
}

export const DAY_STYLES: Record<DayStyle, DayStyleConfig> = {
  "locals-best": {
    ...NEUTRAL,
    key: "locals-best",
    sport: "ski",
    label: "Recommended for you",
    icon: "*",
    blurb: "A balanced day using forecast conditions, your ability and the mapped route home.",
    summaryLine: "Chosen for your ability, forecast conditions and available access.",
  },
  "cruisy-classics": {
    ...NEUTRAL,
    key: "cruisy-classics",
    sport: "ski",
    label: "Cruisy classics",
    icon: "~",
    blurb: "Long, flowing, satisfying runs. The kind you talk about at dinner, whatever their colour.",
    summaryLine: "Long flowing classics over maximum difficulty, with easy links between them.",
    objectives: { cruise: 0.5 },
    difficultyFit: { byDelta: { "-4": 0.9, "-3": 1, "-2": 1.1, "-1": 1.05, "0": 0.85, "+1": 0.7 } },
    noveltyBase: 0.5,
    repeatSoftCap: 0.75,
    stopBiasMultiplier: 1.1,
    transferPenaltyMultiplier: 1.5,
    surfaceBias: { groomed: 1.08 },
    prefPatch: patch({ poling: "minimise" } as Partial<PlannerPreferences>),
  },
  "steep-technical": {
    ...NEUTRAL,
    key: "steep-technical",
    sport: "ski",
    label: "Steep and technical",
    icon: "^",
    blurb: "Pitch, moguls and technical lines first, when today's conditions hold up on them.",
    summaryLine: "Prioritising pitch and technical terrain where conditions justify it.",
    difficultyFit: { byDelta: { "-4": 0.6, "-3": 0.7, "-2": 0.8, "-1": 1.15, "0": 1.15, "+1": 1.15 } },
    stopBiasMultiplier: 0.9,
    surfaceBias: { ungroomed: 1.05 },
    prefPatch: patch({ difficultyMode: "push" } as Partial<PlannerPreferences>),
  },
  "cover-ground": {
    ...NEUTRAL,
    key: "cover-ground",
    sport: "ski",
    label: "Cover ground",
    icon: ">",
    blurb: "A grand tour. More sectors, classic links and that big-day feeling.",
    summaryLine: "A tour of the mountain: more sectors and classic links over lapping one hill.",
    objectives: { coverage: 0.8 },
    noveltyBase: 0.3,
    repeatSoftCap: 0.5,
    sectorEntryBonus: 15,
    transferPenaltyMultiplier: 0.7,
    prefPatch: patch({ distance: "max" } as Partial<PlannerPreferences>),
  },
  "quiet-day": {
    ...NEUTRAL,
    key: "quiet-day",
    sport: "ski",
    label: "Quiet day",
    icon: "q",
    blurb: "An estimated quieter route that steers around the busiest lifts at their busiest times.",
    summaryLine: "Routed around the estimated busiest lifts, trading a little convenience for calm.",
    objectives: { quiet: 0.85 },
    queueWeight: 2,
    stopBiasMultiplier: 1.1,
    transferPenaltyMultiplier: 0.9,
  },
  "sun-and-views": {
    ...NEUTRAL,
    key: "sun-and-views",
    sport: "ski",
    label: "Sun and views",
    icon: "o",
    blurb: "Follows the sun and the big panoramas across the day.",
    summaryLine: "Chasing sun and views; it may trade a few score points for the panorama, and says when it does.",
    objectives: { sun: W, scenic: W },
    stopBiasMultiplier: 1.2,
    prefPatch: patch({ startTime: "08:15" } as Partial<PlannerPreferences>),
  },
  "easy-social": {
    ...NEUTRAL,
    key: "easy-social",
    sport: "ski",
    label: "Easy social day",
    icon: "+",
    blurb: "Gentler terrain, easy links and proper stops. Built for mixed groups.",
    summaryLine: "A sociable day: gentler runs, no awkward links, and the stops given room to breathe.",
    objectives: { cruise: 0.4 },
    difficultyFit: { byDelta: { "-4": 1, "-3": 1.05, "-2": 1.1, "-1": 0.95, "0": 0.7 } },
    keepGreens: true,
    noveltyBase: 0.55,
    repeatSoftCap: 0.8,
    queueWeight: 1.3,
    stopBiasMultiplier: 1.5,
    transferPenaltyMultiplier: 1.5,
    prefPatch: patch({
      difficultyMode: "comfort",
      lunch: { style: "sit-down" },
      coffeeStops: "both",
    } as Partial<PlannerPreferences>),
  },
  "best-snow": {
    ...NEUTRAL,
    key: "best-snow",
    sport: "ski",
    label: "Best snow only",
    icon: "*",
    blurb: "Conditions first. Follows today's best snow wherever it is, even if the day is less varied.",
    summaryLine: "Snow first: the day follows the best-scoring terrain and accepts a narrower shape.",
    appealWeight: 0.5,
    noveltyBase: 0.4,
    stopBiasMultiplier: 0.9,
    transferPenaltyMultiplier: 0.8,
  },
  "locals-best-ride": {
    ...NEUTRAL,
    key: "locals-best-ride",
    sport: "mtb",
    label: "Recommended for you",
    icon: "*",
    blurb: "A ride matched to your ability, bike and forecast trail conditions.",
    summaryLine: "A ride matched to your ability, bike and forecast trail conditions.",
  },
  "flow-day": {
    ...NEUTRAL,
    key: "flow-day",
    sport: "mtb",
    label: "Flow day",
    icon: "~",
    blurb: "Smooth, flowing trails that keep the wheels rolling.",
    summaryLine: "Smooth, flowing trails that keep the wheels rolling.",
    objectives: { cruise: 0.5 },
    difficultyFit: { byDelta: { "-4": 0.95, "-3": 1.05, "-2": 1.1, "-1": 1, "0": 0.8, "+1": 0.65 } },
    noveltyBase: 0.5,
    transferPenaltyMultiplier: 1.3,
    prefPatch: patch({ ridingStyle: "flow" } as Partial<PlannerPreferences>),
  },
  "technical-enduro": {
    ...NEUTRAL,
    key: "technical-enduro",
    sport: "mtb",
    label: "Technical enduro",
    icon: "^",
    blurb: "Steep, rough and technical where today's dirt holds up.",
    summaryLine: "Steep, rough and technical where today's dirt holds up.",
    difficultyFit: { byDelta: { "-4": 0.6, "-3": 0.7, "-2": 0.85, "-1": 1, "0": 1.15, "+1": 1.15 } },
    prefPatch: patch({
      ridingStyle: "technical-enduro",
      difficultyMode: "push",
    } as Partial<PlannerPreferences>),
  },
  "big-descents": {
    ...NEUTRAL,
    key: "big-descents",
    sport: "mtb",
    label: "Big descents",
    icon: "v",
    blurb: "Long lift-served descents, allowing short connecting climbs.",
    summaryLine: "Prioritises long lift-served descents with a little pedalling between them.",
    difficultyFit: { byDelta: { "0": 1.1 } },
    transferPenaltyMultiplier: 0.8,
    dropBonus: true,
    prefPatch: patch({
      ridingStyle: "downhill-park",
      climbingTolerance: "some-pedalling",
    } as Partial<PlannerPreferences>),
  },
  "xc-adventure": {
    ...NEUTRAL,
    key: "xc-adventure",
    sport: "mtb",
    label: "XC and adventure",
    icon: ">",
    blurb: "A proper tour under your own steam: more network, honest climbing, varied trails.",
    summaryLine: "A proper tour under your own steam: more network, honest climbing, varied trails.",
    objectives: { coverage: W },
    difficultyFit: { byDelta: { "-2": 1.1, "-1": 1.05, "0": 0.85, "+1": 0.7 } },
    sectorEntryBonus: 12,
    transferPenaltyMultiplier: 0.7,
    prefPatch: patch({
      climbingTolerance: "happy-to-climb",
      distance: "max",
    } as Partial<PlannerPreferences>),
  },
  "quiet-uplift": {
    ...NEUTRAL,
    key: "quiet-uplift",
    sport: "mtb",
    label: "Quiet uplift",
    icon: "q",
    blurb: "Routed around the estimated busiest uplifts.",
    summaryLine: "Routed around the estimated busiest uplifts.",
    objectives: { quiet: 0.85 },
    queueWeight: 2,
  },
  "easy-social-ride": {
    ...NEUTRAL,
    key: "easy-social-ride",
    sport: "mtb",
    label: "Easy social ride",
    icon: "+",
    blurb: "Mellow trails, easy links and proper stops for a mixed group.",
    summaryLine: "Mellow trails, easy links and proper stops for a mixed group.",
    objectives: { cruise: 0.4 },
    difficultyFit: { byDelta: { "-3": 1.05, "-2": 1.1, "-1": 0.95, "0": 0.7 } },
    keepGreens: true,
    noveltyBase: 0.55,
    repeatSoftCap: 0.8,
    stopBiasMultiplier: 1.5,
    transferPenaltyMultiplier: 1.5,
    prefPatch: patch({
      difficultyMode: "comfort",
      lunch: { style: "sit-down" },
      coffeeStops: "both",
    } as Partial<PlannerPreferences>),
  },
  "best-dirt": {
    ...NEUTRAL,
    key: "best-dirt",
    sport: "mtb",
    label: "Best dirt only",
    icon: "*",
    blurb: "Dirt first. Follows today's best-running trails and accepts a narrower shape.",
    summaryLine: "Dirt first: the ride follows the best-scoring trails and accepts a narrower shape.",
    appealWeight: 0.5,
    noveltyBase: 0.4,
    stopBiasMultiplier: 0.9,
    transferPenaltyMultiplier: 0.8,
  },
};

export const TOUCHED_FIELD_PATHS = [
  "dayStyle",
  "date",
  "startTime",
  "endTime",
  "ability",
  "difficultyMode",
  "distance",
  "liftPrefs.avoidDrags",
  "liftPrefs.preferGondolas",
  "liftPrefs.allowedTypes.drags",
  "liftPrefs.allowedTypes.gondolas",
  "liftPrefs.allowedTypes.chairs",
  "liftPrefs.preferFastLifts",
  "lunch.time",
  "lunch.style",
  "lunch.price",
  "coffeeStops",
  "apres",
  "group",
  "returnToStart",
  "includeUnknownLifts",
  "startPoint",
  "finishPoint",
  "requiredStop",
  "skiPass",
  "allowBus",
  "objectives.sun",
  "objectives.scenic",
  "objectives.firstTracks",
  "objectives.coverage",
  "objectives.cruise",
  "objectives.quiet",
  "poling",
  "treeRuns",
  "ridingStyle",
  "fitness",
  "climbingTolerance",
  "liftAccess",
  "bikeType",
] as const;

const TOUCHED_FIELD_SET = new Set<string>(TOUCHED_FIELD_PATHS);

const STYLE_PATCH_PATHS: Partial<Record<DayStyle, readonly string[]>> = {
  "cruisy-classics": ["poling"],
  "steep-technical": ["difficultyMode"],
  "cover-ground": ["distance"],
  "sun-and-views": ["startTime"],
  "easy-social": ["difficultyMode", "lunch.style", "coffeeStops"],
  "flow-day": ["ridingStyle"],
  "technical-enduro": ["ridingStyle", "difficultyMode"],
  "big-descents": ["ridingStyle", "climbingTolerance"],
  "xc-adventure": ["climbingTolerance", "distance"],
  "easy-social-ride": ["difficultyMode", "lunch.style", "coffeeStops"],
};

export function dayStylesForSport(sport: AreaSport): DayStyleConfig[] {
  return Object.values(DAY_STYLES).filter((style) => style.sport === sport);
}

export function defaultDayStyleForSport(sport: AreaSport): DayStyle {
  return sport === "mtb" ? "locals-best-ride" : "locals-best";
}

export function isValidDayStyle(sport: AreaSport, key: string): key is DayStyle {
  return dayStylesForSport(sport).some((style) => style.key === key);
}

export function coerceTouchedFields(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const value of raw) {
    if (out.length >= 40) break;
    if (typeof value !== "string") continue;
    if (value.length > 48) continue;
    if (!TOUCHED_FIELD_SET.has(value)) continue;
    out.push(value);
  }
  return out;
}

export function stylePatchPathsFor(key: DayStyle): string[] {
  return [...(STYLE_PATCH_PATHS[key] ?? [])];
}

function earlierStart(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const mins = Math.max(8 * 60, (h || 0) * 60 + (m || 0) - 45);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

function nextDistance(distance: PlannerPreferences["distance"]): PlannerPreferences["distance"] {
  if (distance === "short") return "half-day";
  if (distance === "half-day") return "full-day";
  if (distance === "full-day") return "max";
  return "max";
}

function getPathValue(prefs: PlannerPreferences, path: string): unknown {
  switch (path) {
    case "startTime":
      return prefs.startTime;
    case "difficultyMode":
      return prefs.difficultyMode;
    case "distance":
      return prefs.distance;
    case "poling":
      return prefs.sport === "ski" ? prefs.poling : undefined;
    case "lunch.time":
      return prefs.lunch.time;
    case "lunch.style":
      return prefs.lunch.style;
    case "coffeeStops":
      return prefs.coffeeStops;
    case "ridingStyle":
      return prefs.sport === "mtb" ? prefs.ridingStyle : undefined;
    case "climbingTolerance":
      return prefs.sport === "mtb" ? prefs.climbingTolerance : undefined;
    default:
      return undefined;
  }
}

function valuesMatch(a: unknown, b: unknown): boolean {
  return a === b;
}

type PatchMode =
  | { kind: "touched"; fields: ReadonlySet<string> }
  | { kind: "default-values"; defaults: PlannerPreferences };

function shouldPatch(prefs: PlannerPreferences, path: string, mode: PatchMode): boolean {
  if (mode.kind === "touched") return !mode.fields.has(path);
  return valuesMatch(getPathValue(prefs, path), getPathValue(mode.defaults, path));
}

function patchSkiPath(
  prefs: PlannerPreferences,
  path: string,
  value: string,
  mode: PatchMode,
): PlannerPreferences {
  if (prefs.sport !== "ski" || !shouldPatch(prefs, path, mode)) return prefs;
  switch (path) {
    case "startTime":
      return { ...prefs, startTime: value };
    case "difficultyMode":
      return { ...prefs, difficultyMode: value as PlannerPreferences["difficultyMode"] };
    case "distance":
      return { ...prefs, distance: value as PlannerPreferences["distance"] };
    case "poling":
      return { ...prefs, poling: value as typeof prefs.poling };
    case "lunch.time":
      return { ...prefs, lunch: { ...prefs.lunch, time: value } };
    case "lunch.style":
      return { ...prefs, lunch: { ...prefs.lunch, style: value as typeof prefs.lunch.style } };
    case "coffeeStops":
      return { ...prefs, coffeeStops: value as PlannerPreferences["coffeeStops"] };
    default:
      return prefs;
  }
}

function patchMtbPath(
  prefs: PlannerPreferences,
  path: string,
  value: string,
  mode: PatchMode,
): PlannerPreferences {
  if (prefs.sport !== "mtb" || !shouldPatch(prefs, path, mode)) return prefs;
  switch (path) {
    case "difficultyMode":
      return { ...prefs, difficultyMode: value as PlannerPreferences["difficultyMode"] };
    case "distance":
      return { ...prefs, distance: value as PlannerPreferences["distance"] };
    case "lunch.style":
      return { ...prefs, lunch: { ...prefs.lunch, style: value as typeof prefs.lunch.style } };
    case "coffeeStops":
      return { ...prefs, coffeeStops: value as PlannerPreferences["coffeeStops"] };
    case "ridingStyle":
      return { ...prefs, ridingStyle: value as typeof prefs.ridingStyle };
    case "climbingTolerance":
      return { ...prefs, climbingTolerance: value as typeof prefs.climbingTolerance };
    default:
      return prefs;
  }
}

function applyStylePatch(
  prefs: PlannerPreferences,
  key: DayStyle,
  mode: PatchMode,
): PlannerPreferences {
  let next = prefs;
  switch (key) {
    case "cruisy-classics":
      next = patchSkiPath(next, "poling", "minimise", mode);
      break;
    case "steep-technical":
      next = patchSkiPath(next, "difficultyMode", "push", mode);
      break;
    case "cover-ground":
      if (next.sport === "ski" && shouldPatch(next, "distance", mode)) {
        next = { ...next, distance: nextDistance(next.distance) };
      }
      break;
    case "sun-and-views":
      if (next.sport === "ski" && shouldPatch(next, "startTime", mode)) {
        next = { ...next, startTime: earlierStart(next.startTime) };
      }
      break;
    case "easy-social":
      next = patchSkiPath(next, "difficultyMode", "comfort", mode);
      next = patchSkiPath(next, "lunch.style", "sit-down", mode);
      next = patchSkiPath(next, "coffeeStops", "both", mode);
      break;
    case "flow-day":
      next = patchMtbPath(next, "ridingStyle", "flow", mode);
      break;
    case "technical-enduro":
      next = patchMtbPath(next, "ridingStyle", "technical-enduro", mode);
      next = patchMtbPath(next, "difficultyMode", "push", mode);
      break;
    case "big-descents":
      next = patchMtbPath(next, "ridingStyle", "downhill-park", mode);
      next = patchMtbPath(next, "climbingTolerance", "some-pedalling", mode);
      break;
    case "xc-adventure":
      next = patchMtbPath(next, "climbingTolerance", "happy-to-climb", mode);
      if (next.sport === "mtb" && shouldPatch(next, "distance", mode)) {
        next = { ...next, distance: nextDistance(next.distance) };
      }
      break;
    case "easy-social-ride":
      next = patchMtbPath(next, "difficultyMode", "comfort", mode);
      next = patchMtbPath(next, "lunch.style", "sit-down", mode);
      next = patchMtbPath(next, "coffeeStops", "both", mode);
      break;
    default:
      break;
  }
  return next;
}

export function applyDayStyle(
  prefs: PlannerPreferences,
  key: DayStyle,
  touchedFields: readonly string[] = [],
): PlannerPreferences {
  if (!isValidDayStyle(prefs.sport, key)) return prefs;
  const next = {
    ...prefs,
    dayStyle: key,
  } as PlannerPreferences;
  return applyStylePatch(next, key, {
    kind: "touched",
    fields: new Set(touchedFields),
  });
}

export function applyDayStyleForCoercion(
  prefs: PlannerPreferences,
  key: DayStyle,
  touchedFields: readonly string[] | undefined,
  defaults: PlannerPreferences,
): PlannerPreferences {
  if (!isValidDayStyle(prefs.sport, key)) return prefs;
  const next = {
    ...prefs,
    dayStyle: key,
  } as PlannerPreferences;
  return applyStylePatch(
    next,
    key,
    touchedFields
      ? { kind: "touched", fields: new Set(touchedFields) }
      : { kind: "default-values", defaults },
  );
}
